import React, { useCallback, useEffect, useState } from "react";
import api from "../../api";
import { Stat } from "../../components/bits";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const List = ({ title, items, testId }) => (
  <section className="cr-card !p-4" data-testid={testId}>
    <h3 className="text-xs font-bold mb-2.5">{title}</h3>
    {(!items || items.length === 0) && <p className="text-[11px] text-[#A09D95]">No data yet — appears as visitors use the site.</p>}
    <div className="space-y-1">
      {(items || []).map((r) => (
        <div key={r.key} className="flex items-center justify-between text-xs border-b border-[#F6F3EA] last:border-0 py-1.5">
          <span className="truncate text-[#1C1B1A]">{r.key}</span>
          <span className="font-semibold ml-3 shrink-0">{r.n}</span>
        </div>
      ))}
    </div>
  </section>
);

export function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/analytics", { params: { days } }).then((r) => setData(r.data)).catch(() => {});
  }, [days]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="ct-analytics">
      <div className="flex items-center justify-between">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Unique visitors" value={data.visitors} sub={`last ${data.days} days`} />
          <Stat label="Page views" value={data.pageviews} sub={`last ${data.days} days`} />
        </div>
        <select data-testid="analytics-days" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="cr-input !w-32 !py-1.5 !text-xs">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <section className="cr-card !p-4" data-testid="analytics-chart">
        <h3 className="text-xs font-bold mb-2">Traffic</h3>
        {data.daily.length === 0 ? (
          <p className="text-[11px] text-[#A09D95]">No visits recorded in this period.</p>
        ) : (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#A09D95" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#A09D95" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 14, border: "1px solid #E8E4D9" }} />
                <Area type="monotone" dataKey="views" stroke="#1C1B1A" strokeWidth={2} fill="#F2D06B" fillOpacity={0.35} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <List title="Top pages" items={data.pages} testId="analytics-pages" />
        <List title="Countries" items={data.countries} testId="analytics-countries" />
        <List title="Devices" items={data.devices} testId="analytics-devices" />
        <List title="Referrers" items={data.referrers} testId="analytics-referrers" />
      </div>
      <p className="text-[11px] text-[#A09D95]">Privacy-friendly first-party analytics: no cookies, no third-party trackers. Bots excluded. Country comes from the CDN header.</p>
    </div>
  );
}
